import { S3_PUBLIC } from '@advanza/constants'
import PopUpNotification from '@advanza/interfaces/PopUpNotification'
import { Divider } from '@advanza/ui'
import ReactMarkdown from 'react-markdown'

export const providerNotificationImageUrl = (notification: PopUpNotification): string | null => {
    if (!notification.header_image) return null

    return `${S3_PUBLIC()}provider_notifications/${notification.pop_up_notification_id}/${
        notification.header_image
    }`
}

/**
 * Office authors often write a bare domain such as `trustoo.nl`, which a browser would resolve
 * against the dashboard's own origin. Anything carrying a scheme (`https:`, `mailto:`, `tel:`) or
 * addressing this app (`/leads`, `#section`, `?tab=x`, `//cdn.example.com`) is already resolvable.
 */
const resolveHref = (href?: string): string | undefined => {
    if (!href) return href

    const isSchemeOrInApp = /^([a-z][a-z\d+.-]*:|\/|#|\?)/i.test(href)

    return isSchemeOrInApp ? href : `https://${href}`
}

const opensExternally = (href?: string): boolean => href !== undefined && !/^(\/|#|\?)/.test(href)

interface Props {
    notification: PopUpNotification
    /** Overrides the S3 URL, so an unsaved image in Office can be previewed from its data URL. */
    imageUrl?: string | null
}

/**
 * The body of the provider notification pop-up: header image, label, title and markdown content.
 *
 * Shared between the pros dashboard, which renders it for real, and the Office preview, so the two
 * cannot drift apart. The CTA button stays with the caller because its click behaviour differs.
 */
const ProviderNotificationContent = ({ notification, imageUrl }: Props) => {
    const image = imageUrl === undefined ? providerNotificationImageUrl(notification) : imageUrl

    return (
        <>
            {image && (
                <>
                    <div className="flex justify-center items-center">
                        <figure>
                            <img height={100} src={image} alt={notification.title} />
                        </figure>
                    </div>
                    <Divider m />
                </>
            )}
            <div className="text-link font-bold flex flex-col items-start gap-1 self-stretch">
                {notification.label}
            </div>
            <Divider xs />
            <div
                className="text-primary-vars font-poppins text-lg not-italic font-bold leading-6"
                style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}>
                {notification.title}
            </div>
            <Divider s />
            <span style={{ whiteSpace: 'pre-wrap' }}>
                <ReactMarkdown
                    components={{
                        a: ({ href, children, ...props }) => {
                            const external = opensExternally(href)

                            return (
                                <a
                                    {...props}
                                    href={resolveHref(href)}
                                    target={external ? '_blank' : undefined}
                                    rel={external ? 'noopener noreferrer' : undefined}
                                    className="text-sky-400 underline">
                                    {children}
                                </a>
                            )
                        },
                    }}>
                    {notification.content}
                </ReactMarkdown>
            </span>
        </>
    )
}

export default ProviderNotificationContent
